import React, { useEffect, useRef, useState } from 'react';
import { CopyToClipboard as CopyToClipboardLib } from 'react-copy-to-clipboard';
import { LocalizeContextProps, withLocalize } from 'react-localize-redux';
import { Icon } from 'semantic-ui-react';
import styled from 'styled-components';

interface Props extends LocalizeContextProps {
    text: string;
    showIcon?: boolean;
}

const Main = styled.span`
    position: relative;
    display: inline-flex;
    align-items: center;
    gap: 4px;
`;

const CopyButton = styled.span`
    cursor: pointer;
    opacity: 0.45;
    transition: opacity 0.15s;
    display: inline-flex;
    align-items: center;

    &:hover {
        opacity: 1;
    }

    & > i.icon {
        cursor: pointer;
    }
`;

const Tooltip = styled.span`
    position: absolute;
    left: 100%;
    top: 50%;
    transform: translateY(-50%);
    margin-left: 6px;
    white-space: nowrap;
    padding: 3px 8px;
    background: white;
    border: 1px solid var(--gray-100);
    border-radius: 3px;
    color: var(--blue);
    font-size: 0.85em;
    z-index: 1000;
    pointer-events: none;
    animation: ctcFadeIn 0.15s ease-in;

    @keyframes ctcFadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
    }
`;

const HIDE_TIMEOUT = 1500;

const CopyToClipboard: React.FC<Props> = (props) => {
    const { text, translate } = props;
    const [copied, setCopied] = useState(false);
    const timerRef = useRef<ReturnType<typeof setTimeout>>();

    useEffect(() => {
        return () => {
            if (timerRef.current) {
                clearTimeout(timerRef.current);
            }
        };
    }, []);

    const handleCopy = () => {
        setCopied(true);
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
        timerRef.current = setTimeout(() => setCopied(false), HIDE_TIMEOUT);
    };

    return (
        <Main>
            <span>{props.children}</span>
            <CopyToClipboardLib text={text} onCopy={handleCopy}>
                <CopyButton title={translate('copyToClipboard.clickToCopy') as string}>
                    <Icon
                        name={copied ? 'check' : 'copy outline'}
                        color={copied ? 'green' : undefined}
                        style={{ margin: 0 }}
                    />
                </CopyButton>
            </CopyToClipboardLib>
            {copied && <Tooltip>{translate('copyToClipboard.successfullyCopied')}</Tooltip>}
        </Main>
    );
};

export default withLocalize(CopyToClipboard);
